import { cn } from '@utils/cn'

export type DiamondSize = 'sm' | 'md' | 'lg'

export interface DiamondProps {
  /** The runic glyph shown inside the frame. */
  rune: string
  /** Frame size. Defaults to `sm` (section headers). */
  size?: DiamondSize
  /** Solid gradient fill + glow (brand mark, item headers). Outline-only otherwise. */
  solid?: boolean
  className?: string
}

const SIZE: Record<DiamondSize, { frame: string; glyph: string }> = {
  sm: { frame: 'size-7', glyph: 'text-sm' },
  md: { frame: 'size-8', glyph: 'text-base' },
  lg: { frame: 'size-14', glyph: 'text-2xl' },
}

/**
 * The RuneLedger motif: a 45°-rotated square frame holding a runic glyph. Purely decorative —
 * hidden from the a11y tree, so it must always sit next to visible text, never carry meaning
 * alone. The rotation overflows the box slightly; callers add margin when it sits flush.
 */
export function Diamond({ rune, size = 'sm', solid = false, className }: DiamondProps) {
  const { frame, glyph } = SIZE[size]
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid rotate-45 place-items-center border',
        solid ? 'border-arcane/55 bg-btn-grad shadow-glow-soft' : 'border-arcane/40 bg-arcane/10',
        frame,
        className
      )}
    >
      <span
        className={cn('-rotate-45 font-runic', solid ? 'text-rune-bright' : 'text-rune', glyph)}
      >
        {rune}
      </span>
    </span>
  )
}
